import type { OrderKind } from './types';
import {
  MerchantOrderStatusEnum,
  MerchantOrderTypeEnum,
} from '@/graphql/generatedTypes';

// What an order at its current status lets ops change. Field-level rather than
// one read-only flag because RETURN_TO_SENDER needs the delivery address open —
// it is the thing that has to be corrected before a replacement shipment can be
// created — while items, weights and carrier are settled: the parcels were
// already picked and shipped once.
//
// The notification language travels with the address rather than with the rest:
// a corrected address can be in another country, and the replacement's emails
// should follow the recipient.
export interface OrderFieldPermissions {
  canEditAddress: boolean;
  canEditItems: boolean;
  canEditPackageWeights: boolean;
  canEditCarrier: boolean;
  // Locked with the carrier: once approved, the order is on its way into the
  // picking job of the warehouse it already has.
  canEditWarehouse: boolean;
  canEditStatus: boolean;
  canEditLanguage: boolean;
}

interface OrderFormPolicy extends OrderFieldPermissions {
  orderKind: OrderKind;
  /**
   * Every field is locked. Kept as the single flag callers that treat the form
   * as one unit still use; a field-specific caller should read the permission
   * it actually needs.
   */
  isReadOnly: boolean;
}

const INBOUND_ORDER_TYPES = new Set<MerchantOrderTypeEnum>([
  MerchantOrderTypeEnum.Pickup,
  MerchantOrderTypeEnum.Termination,
  MerchantOrderTypeEnum.Inventory,
  MerchantOrderTypeEnum.DpdPickup,
  MerchantOrderTypeEnum.DpdShopReturn,
]);

const LOCKED_STATUSES = new Set<MerchantOrderStatusEnum>([
  MerchantOrderStatusEnum.Approved,
  MerchantOrderStatusEnum.SalesOrderSent,
  MerchantOrderStatusEnum.SentToPicking,
  MerchantOrderStatusEnum.Picked,
  MerchantOrderStatusEnum.InTransit,
  MerchantOrderStatusEnum.Delivered,
  MerchantOrderStatusEnum.Completed,
  MerchantOrderStatusEnum.ReturnToSender,
]);

const EDITABLE: OrderFieldPermissions = {
  canEditAddress: true,
  canEditItems: true,
  canEditPackageWeights: true,
  canEditCarrier: true,
  canEditWarehouse: true,
  canEditStatus: true,
  canEditLanguage: true,
};

const LOCKED: OrderFieldPermissions = {
  canEditAddress: false,
  canEditItems: false,
  canEditPackageWeights: false,
  canEditCarrier: false,
  canEditWarehouse: false,
  canEditStatus: false,
  canEditLanguage: false,
};

// Address-only, and the ordinary status selector stays shut: the only ways out
// of RETURN_TO_SENDER are createReplacementSendCloudShipment and the picking
// job's resolvePickingJob (which cancels the order as part of accounting for
// its stock). Neither is a status change, so neither is a dropdown choice.
const RETURN_TO_SENDER_PERMISSIONS: OrderFieldPermissions = {
  ...LOCKED,
  canEditAddress: true,
  canEditLanguage: true,
};

export function isStatusLocked(
  status?: MerchantOrderStatusEnum | null,
): boolean {
  return !!status && LOCKED_STATUSES.has(status);
}

export function isReturnedToSender(
  status?: MerchantOrderStatusEnum | null,
): boolean {
  return status === MerchantOrderStatusEnum.ReturnToSender;
}

export function getOrderKind(
  orderType?: MerchantOrderTypeEnum | null,
  status?: MerchantOrderStatusEnum | null,
): OrderFormPolicy {
  const orderKind: OrderKind =
    orderType && INBOUND_ORDER_TYPES.has(orderType) ? 'inbound' : 'outbound';

  const permissions =
    orderKind === 'inbound'
      ? LOCKED
      : isReturnedToSender(status)
        ? RETURN_TO_SENDER_PERMISSIONS
        : isStatusLocked(status)
          ? LOCKED
          : EDITABLE;

  return {
    orderKind,
    ...permissions,
    isReadOnly: !Object.values(permissions).some(Boolean),
  };
}
